// @owners { team: patients }
import { COLORS, SIZES, SPACING } from '@alto/design-library-tokens';
import { Column, H1, H3, Illustration, LgPadding, Row, getBrand } from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import styledNative from 'styled-components/native';
import { type Service, getFertilityValueProps } from '~shared/features/onboarding/constants';
import { FERTILITY_SUPPORT } from '~shared/features/onboarding/helpers/onboardingRouteHandler';
import { getIsCurrentUserFertility } from '~shared/features/users/selectors/getIsFertilityUser';
import { useSelectorShared } from '~shared/store';
import { OnboardingNavBarLogo } from './OnboardingNavBarLogo';
import { nextUrl } from '~web/features/onboarding/helpers/urlHandler';

type AnimatedConnectorProps = {
  isVisible: boolean;
  delay: number;
};

type ServiceRowProps = {
  service: Service;
  isVisible: boolean;
  delay: number;
};

const PageContainer = styledNative(Column).attrs({
  centerHorizontally: true,
})<{ showFertility: boolean }>`
  height: 100vh;
  padding-top: ${SPACING.STATIC.MD.px};
  background-color: ${({ showFertility }) =>
    showFertility ? COLORS.BACKGROUND_COLORS.TERTIARY_LIGHTEST : COLORS.BACKGROUND_COLORS.PINK_LIGHTEST};
`;

const ConnectorLine = styled.div<{ isVisible: boolean; delay: number; backgroundColor?: string }>`
  width: 4px;
  height: 45px;
  background-color: ${({ backgroundColor }) => backgroundColor};
  transform-origin: top;
  transition: all 1.5s ease-out;
  transition-delay: ${(props) => (props.isVisible ? '0ms' : `${props.delay}ms`)};
  transform: ${(props) => (props.isVisible ? 'scaleY(1)' : 'scaleY(0)')};
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
`;

const getBackgroundColor = () => {
  const brand = getBrand();
  if (brand === 'fuzehealth') {
    return COLORS.BACKGROUND_COLORS.SECONDARY;
  }
  return '#00897b';
};

const AnimatedConnector: React.FC<AnimatedConnectorProps> = ({ isVisible, delay }) => {
  return (
    <Row>
      <LgPadding
        topPadding={SPACING.STATIC.NONE}
        rightPadding={SPACING.STATIC.NONE}
        bottomPadding={SPACING.STATIC.NONE}
      >
        <Row
          style={{ width: SIZES.ILLUSTRATION.MD.value }}
          centerHorizontally
        >
          <ConnectorLine
            backgroundColor={getBackgroundColor()}
            isVisible={isVisible}
            delay={delay}
          />
        </Row>
      </LgPadding>
    </Row>
  );
};

const ServiceRowContainer = styled.div<{ isVisible: boolean; delay: number }>`
  display: flex;
  align-items: center;
  gap: ${SPACING.STATIC.LG.px};
  padding: ${SPACING.STATIC.SM.px} ${SPACING.STATIC.LG.px};
  transition: all 0.7s ease-out;
  transition-delay: ${(props) => (props.isVisible ? '0ms' : `${props.delay}ms`)};
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  transform: ${(props) => (props.isVisible ? 'translateY(0)' : 'translateY(32px)')};
`;

const ServiceRow: React.FC<ServiceRowProps> = ({ service, isVisible, delay }) => {
  return (
    <ServiceRowContainer
      isVisible={isVisible}
      delay={delay}
    >
      <Row flexShrink={0}>
        <Illustration
          accessibilityLabel={service.title}
          source={service.icon}
        />
      </Row>
      <Row flexGrow={1}>
        <H3>{service.title}</H3>
      </Row>
    </ServiceRowContainer>
  );
};

export const FertilitySupport = () => {
  const { navigate } = useNavigation();
  const [visibleRows, setVisibleRows] = useState<number[]>([]);
  const [visibleConnectors, setVisibleConnectors] = useState<number[]>([]);
  const [goToNextUrl, setGoToNextUrl] = useState(false);
  const showFertility = useSelectorShared(getIsCurrentUserFertility);
  const nextRoute = nextUrl({ currentRoute: FERTILITY_SUPPORT });
  const services = getFertilityValueProps();

  useEffect(() => {
    const timer = setTimeout(() => {
      let currentIndex = 0;

      const showNextSequence = () => {
        if (currentIndex >= services.length) {
          setGoToNextUrl(true);
          return;
        }

        setVisibleRows((prev) => [...prev, currentIndex]);

        setTimeout(() => {
          setVisibleConnectors((prev) => [...prev, currentIndex]);

          setTimeout(() => {
            currentIndex++;
            showNextSequence();
          }, 1500);
        }, 700);
      };

      showNextSequence();
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [services.length]);

  useEffect(() => {
    if (goToNextUrl) {
      navigate(nextRoute);
    }
  }, [goToNextUrl, navigate, nextRoute]);

  return (
    <>
      <PageContainer showFertility={showFertility}>
        <Column
          centerHorizontally
          flexGrow={1}
          gap="XL"
        >
          <OnboardingNavBarLogo />
          <Column>
            <LgPadding
              topPadding={SPACING.STATIC.NONE}
              bottomPadding={SPACING.STATIC.NONE}
            >
              <H1 center>Supporting you at every step of the journey</H1>
            </LgPadding>
          </Column>

          <Column>
            {services.map((service, index) => (
              <Column key={service.id}>
                <ServiceRow
                  service={service}
                  isVisible={visibleRows.includes(index)}
                  delay={0}
                />
                {index < services.length - 1 && (
                  <AnimatedConnector
                    isVisible={visibleConnectors.includes(index)}
                    delay={0}
                  />
                )}
              </Column>
            ))}
          </Column>
        </Column>
      </PageContainer>
    </>
  );
};
